import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { AppState } from '../store'
import styled from 'styled-components'
import { removeSelection, clearSelection } from '../store/selection/actions'

const TextLink = styled.button`
  display: inline-block;
  padding: 0;
  border: none;
  font: inherit;
  color: #56a0d3;
  background-color: transparent;
  cursor: pointer;

  &:hover {
    color: #3388c3;
  }
`

const Mediaselector = styled.div<{ active: boolean }>`
  transition: bottom 200ms;
  bottom: ${(p) => (p.active ? 0 : -60)}px;
`

export const Selection: React.FC = () => {
  const [active, setActive] = useState(false)

  const mediaselectorTriggerWrapper = React.useRef<HTMLDivElement>(null)
  const mediaselectorContent = React.useRef<HTMLDivElement>(null)

  useEffect(() => {
    window.$('html, body').on('touchstart click', (e: any) => {
      if (window.$(e.target).closest('.mediaselector-content, .overlay-blocker, .mediaselector-header').length === 0) {
        setActive(false)
      }
    })
  }, [mediaselectorTriggerWrapper])

  const dispatch = useDispatch()
  const selection = useSelector((p: AppState) => p.selectionsReducer.selection)
  const previousSelectionLength = React.useRef(selection.length)

  useEffect(() => {
    if (!active && previousSelectionLength.current === selection.length) {
      window.$(mediaselectorContent.current).css({ maxHeight: '' })
    } else if (active && selection.length === 0) {
      setActive(false)
    } else if (
      !active &&
      selection.length > 0 &&
      mediaselectorContent.current !== null &&
      previousSelectionLength.current !== selection.length
    ) {
      window.$(mediaselectorContent.current).css({ maxHeight: '1000px' })

      const animationHeight = mediaselectorContent.current!.scrollHeight
      window.$(mediaselectorContent.current).css({ maxHeight: `${animationHeight}px` })

      setTimeout(() => {
        window.$(mediaselectorContent.current).css({ maxHeight: '' })
      }, 2000)
    }
    previousSelectionLength.current = selection.length
  }, [active, selection, mediaselectorContent])

  return (
    <>
      <section
        className={active ? 'mediaselector-wrapper mediaselector-wrapper--active' : 'mediaselector-wrapper'}
        ref={mediaselectorTriggerWrapper}
      >
        <Mediaselector className="mediaselector" active={selection.length > 0}>
          <div className="grid-outer-container">
            <div className="grid-row">
              <div className="grid-span6 grid-shift6 grid-tablet-span9 grid-tablet-shift3 grid-mobile-span12 grid-mobile-shift0 helper-relative">
                <div className="mediaselector-container">
                  <header
                    className="mediaselector-header"
                    onClick={() => setActive(!active)}
                    dangerouslySetInnerHTML={
                      selection.length === 1
                        ? {
                            __html: window.translations['Material - 1 document selected'].replace(
                              '1',
                              '<span class="mediaselector-count">1</span>'
                            )
                          }
                        : {
                            __html: window.translations['Material - x documents selected'].replace(
                              '$count$',
                              `<span class="mediaselector-count">${selection.length}</span>`
                            )
                          }
                    }
                  />
                  <section className="mediaselector-content" ref={mediaselectorContent} style={{ overflowY: 'scroll' }}>
                    <div className="mediaselector-content-inner">
                      <div className="grid-row grid-gap1-below">
                        <div className="grid-span3 grid-desktop-span4 grid-mobile-span5">
                          {window.translations['Material - Your selection']} ({selection.length})
                        </div>
                        <div className="grid-span5 grid-desktop-span4 grid-mobile-span3">
                          <TextLink onClick={() => dispatch(clearSelection())}>
                            {window.translations['Material - Clear all']}
                          </TextLink>
                        </div>

                        <div className="grid-span4 grid-mobile-span4 grid-text-right">
                          <TextLink
                            onClick={() => {
                              const form = window.$(`#${window.downloadFormGuid} form`)

                              form.show()
                              window.$('.helper-hide-after-hubspot-submit').show()
                              window.$('.helper-show-after-hubspot-submit').hide()

                              form
                                .find('input[name=hq_material_downloaded]')
                                .closest('.grid-row')
                                .hide()
                              form
                                .find('input[name=hq_language_downloaded_material]')
                                .closest('.grid-row')
                                .hide()
                              form
                                .find('input[name=hq_action_on_product_page]')
                                .closest('.grid-row')
                                .hide()

                              form.unbind('submit').submit((e: any) => {
                                e.preventDefault()

                                const formData = new FormData()

                                // serialize the form, s.t. every field from the HubSpot form is serialized
                                for (const { name, value } of form.serializeArray()) {
                                  formData.append(name, value)
                                }

                                // add fixed values
                                formData.append('subject', window.mailContent.subject)
                                formData.append('body', window.mailContent.content)
                                formData.append(
                                  'attachmentIds',
                                  Object.values(selection)
                                    .map((i) => parseInt(i.file, 10))
                                    .join(',')
                                )
                                formData.append(
                                  'languages',
                                  Object.values(selection)
                                    .map((i) => i.language)
                                    .join(',')
                                )
                                formData.append('hubspotFormGuid', window.downloadFormGuid)

                                fetch('/umbraco/surface/DownloadCenter/SendMaterialsMail2/', {
                                  method: 'POST',
                                  body: formData
                                })
                                  .then((response) => {
                                    if (response.ok) {
                                      // this will call the 'onsubmit' attribute on the form
                                      Function(form.attr('onsubmit')).call(form)

                                      form.hide()
                                      window.$('.helper-hide-after-hubspot-submit').hide()
                                      window.$('.helper-show-after-hubspot-submit').show()

                                      dispatch(clearSelection()) // clear the selection
                                    } else {
                                      alert('Something went wrong - please try again')
                                    }
                                  })
                                  .catch(() => {
                                    alert('Something went wrong - please try again')
                                  })

                                return false
                              })

                              window.openDialog(`.${window.downloadFormGuid}`)
                            }}
                            className="mediaselector-tool"
                          >
                            <i className="fa fa-cloud-download" aria-hidden="true" /> Download
                          </TextLink>
                        </div>
                      </div>
                      <div className="grid-row">
                        <div className="grid-span12">
                          <div className="mediaselector-media-wrapper">
                            {selection.map((item) => (
                              <div key={item.title} className="mediaselector-media" style={{ marginRight: '0.5em' }}>
                                {item.title}
                                <span
                                  className="mediaselector-media-close"
                                  onClick={() => dispatch(removeSelection(item.title))}
                                >
                                  <img src="https://www.universal-robots.com/img/minus_orange.svg" alt="" />
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </div>
        </Mediaselector>
        <div className="mediaselector-blocker" />
      </section>
    </>
  )
}
